import { captureException as sentryCaptureException, setTag } from '@sentry/react';
import axios, {
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  Method,
  RawAxiosRequestHeaders,
} from 'axios';
import { IAxiosRetryConfig } from 'axios-retry';
import { compact } from 'lodash/fp';
import objectToFormData from 'object-to-formdata';
import qs from 'qs';

import ApiNetworkError from 'lib/errors/ApiNetworkError';
import handleAxiosError from 'lib/errors/handleAxiosError';
import { ApiEndpoint, ApiListResponse, ApiResource, ApiResponse } from 'lib/interfaces/api';
import { ApiEndpointDefinitions } from 'lib/interfaces/endpoints';
import {
  DeepPartialExhaustive,
  Json,
  RequiredKeysExhaustive,
  RequiredKeysPickExhaustive,
} from 'lib/types';
import { toEndpoint } from 'lib/utils/api';

interface PaginationOptions {
  number?: number;
  size?: number;
}

export interface ApiOptions {
  parent?: ApiResource;
  meta?: { [key: string]: Json | File | object };
  filter?: { [key: string]: Json | undefined };
  page?: PaginationOptions;
  sort?: string;
  query?: string;
  activationToken?: string;
  include?: Array<string>;
  additionalData?: [ApiResource];
  ignoreAccessToken?: boolean;
  contentType?: 'multipart/form-data' | 'application/json';
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream';
  retryOptions?: IAxiosRetryConfig;
  signal?: AbortSignal;
  headers?: AxiosRequestHeaders | RawAxiosRequestHeaders;
  /**
   * When formDataAllowEmptyArrays is true, we send [''] in the payload to represent [].
   * When formDataAllowEmptyArrays is false, we omit the field key entirely when a list is empty,
   * which is interpreted as nil on the server.
   */
  formDataAllowEmptyArrays?: boolean;

  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
}

export default class ApiClient {
  private apiRoot: string;

  constructor(root: string) {
    this.apiRoot = root;
  }

  public listResource<Resource extends ApiResource, Meta = object>(
    endpoint: ApiEndpoint,
    options: ApiOptions = {},
  ): Promise<ApiListResponse<Resource, Meta>> {
    return this.makeRequest(endpoint, 'GET', options);
  }

  public getResource<Resource extends ApiResource, Meta = object>(
    endpoint: ApiEndpoint,
    id: string,
    options: ApiOptions = {},
  ): Promise<ApiResponse<Resource, Meta>> {
    return this.makeRequest(`${endpoint}/${id}`, 'GET', options);
  }

  public getResourceMeta<Meta = object>(
    endpoint: ApiEndpoint,
    metaType: string,
    options: ApiOptions = {},
  ): Promise<ApiResponse<ApiResource, Meta>> {
    return this.makeRequest(`${endpoint}/${metaType}`, 'GET', options);
  }

  public getResourceMetaTyped<Endpoint extends keyof ApiEndpointDefinitions>(
    endpoint: Endpoint,
    options: ApiOptions = {},
  ): Promise<ApiResponse<ApiResource, ApiEndpointDefinitions[Endpoint]>> {
    return this.makeRequest(`${endpoint}`, 'GET', options);
  }

  public getResourceSingleton<Resource extends ApiResource, Meta = object>(
    endpoint: ApiEndpoint,
    options: ApiOptions = {},
  ): Promise<ApiResponse<Resource, Meta>> {
    return this.makeRequest(endpoint, 'GET', options);
  }

  // This acts the same way as getResourceMeta but expects a flat json response
  // instead of a JSON:API spec compliant response.
  public get<JsonResponse = object>(
    endpoint: ApiEndpoint,
    id: string,
    subPath: string,
    options: ApiOptions = {},
  ): Promise<JsonResponse> {
    return this.makeRequest(`${endpoint}/${id}/${subPath}`, 'GET', options);
  }

  // Works the same as the above, just doesn't require subPaths and Ids

  public getEndpoint<JsonResponse = object>(
    endpoint: ApiEndpoint,
    options: ApiOptions = {},
  ): Promise<JsonResponse> {
    return this.makeRequest(endpoint, 'GET', options);
  }

  public getBlob(
    endpoint: ApiEndpoint,
    id: string,
    subPath?: string,
    options: ApiOptions = {},
  ): Promise<Blob> {
    return this.makeRequest(
      // Generates a URL like `${endpoint}/${id}/${subPath}` except
      // removes trailing slash if subpath is undefined.
      compact([endpoint, id, subPath]).join('/'),
      'GET',
      { ...options, responseType: 'blob' },
    );
  }

  public postResourceCreate<
    Resource extends ApiResource,
    Payload extends RequiredKeysPickExhaustive<
      DeepPartialExhaustive<ApiResource>,
      'type'
    > = RequiredKeysExhaustive<DeepPartialExhaustive<Resource>, 'type'>,
    Meta = object,
  >(payload: Payload, options: ApiOptions = {}): Promise<ApiResponse<Resource, Meta>> {
    const resource = payload.type;
    const parent = options.parent;

    const parentUri = (parent && `${toEndpoint(parent.type)}/${parent.id}/`) || '';
    const uri = parentUri + toEndpoint(resource);

    return this.makeRequestWithBody<Resource, Payload, Meta>(uri, 'POST', payload, options);
  }

  public postMultiResourceCreate<
    Resource extends ApiResource,
    Payload extends RequiredKeysPickExhaustive<
      DeepPartialExhaustive<ApiResource>,
      'type'
    > = RequiredKeysExhaustive<DeepPartialExhaustive<Resource>, 'type'>,
    Meta = object,
  >(
    endpoint: ApiEndpoint,
    payload: Payload,
    options: ApiOptions = {},
  ): Promise<ApiResponse<Resource, Meta>> {
    const { parent, additionalData } = options;

    const parentUri = (parent && `${toEndpoint(parent.type)}/${parent.id}/`) || '';
    const uri = parentUri + endpoint;

    return this.makeRequestWithMultipleResources<Resource, Payload, Meta>(
      uri,
      'POST',
      payload,
      additionalData,
      options,
    );
  }

  public deleteResource<Resource extends ApiResource, Meta = object>(
    endpoint: ApiEndpoint,
    id: string,
  ): Promise<ApiResponse<Resource, Meta>> {
    return this.makeRequest(`${endpoint}/${id}`, 'DELETE');
  }

  public deleteResourceWithPayload<
    Resource extends ApiResource,
    Payload extends RequiredKeysPickExhaustive<
      DeepPartialExhaustive<ApiResource>,
      'type'
    > = RequiredKeysExhaustive<DeepPartialExhaustive<Resource>, 'type'>,
    Meta = object,
  >(payload: Payload, endpoint: ApiEndpoint, id: string): Promise<ApiResponse<Resource, Meta>> {
    return this.makeRequestWithBody<Resource, Payload, Meta>(
      `${endpoint}/${id}`,
      'DELETE',
      payload,
    );
  }

  public updateResource<
    Resource extends ApiResource,
    Payload extends RequiredKeysExhaustive<
      Pick<DeepPartialExhaustive<ApiResource>, 'type' | 'id'>,
      'type' | 'id'
    > = RequiredKeysExhaustive<DeepPartialExhaustive<Resource>, 'type' | 'id'>,
    Meta = object,
  >(payload: Payload, options: ApiOptions = {}): Promise<ApiResponse<Resource, Meta>> {
    const resource = payload.type;
    const parent = options.parent;

    const parentUri = parent ? `${toEndpoint(parent.type)}/${parent.id}/` : '';
    const uri = `${parentUri + toEndpoint(resource)}/${payload.id}`;

    return this.makeRequestWithBody<Resource, Payload, Meta>(uri, 'PATCH', payload, options);
  }

  public updateResourceAction<
    Resource extends ApiResource,
    Payload extends RequiredKeysExhaustive<
      Pick<DeepPartialExhaustive<ApiResource>, 'type' | 'id'>,
      'type'
    > = RequiredKeysExhaustive<DeepPartialExhaustive<Resource>, 'type' | 'id'>,
    Meta = object,
  >(
    payload: Payload,
    action: string,
    options: ApiOptions = {},
  ): Promise<ApiResponse<Resource, Meta>> {
    const resource = payload.type;
    const parent = options.parent;

    const parentUri = parent ? `${toEndpoint(parent.type)}/${parent.id}/` : '';
    const uri = `${parentUri + toEndpoint(resource)}/${payload.id}/${action}`;

    return this.makeRequestWithBody<Resource, Payload, Meta>(uri, 'PATCH', payload, options);
  }

  public updateResourceSingleton<
    Resource extends ApiResource,
    Payload extends RequiredKeysPickExhaustive<
      DeepPartialExhaustive<ApiResource>,
      'type'
    > = RequiredKeysExhaustive<DeepPartialExhaustive<Resource>, 'type'>,
    Meta = object,
  >(payload: Payload, options: ApiOptions = {}): Promise<ApiResponse<Resource, Meta>> {
    const resource = payload.type;
    const parent = options.parent;

    const parentUri = (parent && `${toEndpoint(parent.type)}/${parent.id}/`) || '';
    const uri = parentUri + toEndpoint(resource);

    return this.makeRequestWithBody<Resource, Payload, Meta>(uri, 'PATCH', payload, options);
  }

  public postResourceSingletonActionNew<
    Resource extends ApiResource,
    Payload extends RequiredKeysPickExhaustive<DeepPartialExhaustive<ApiResource>, 'type'>,
    Meta = object,
  >(
    endpoint: ApiEndpoint,
    action: string,
    payload?: Payload,
    options: ApiOptions = {},
  ): Promise<ApiResponse<Resource, Meta>> {
    const uri = `${endpoint}/${action}`;

    return this.makeRequestWithBody<Resource, Payload, Meta>(uri, 'POST', payload, options);
  }

  // TODO: [charles] get rid of this, merge into above which specifies endpoint directly
  public postResourceSingletonAction<
    Resource extends ApiResource,
    Payload extends RequiredKeysPickExhaustive<
      DeepPartialExhaustive<ApiResource>,
      'type'
    > = RequiredKeysExhaustive<DeepPartialExhaustive<Resource>, 'type'>,
    Meta = object,
  >(
    payload: Payload,
    action: string,
    options: ApiOptions = {},
  ): Promise<ApiResponse<Resource, Meta>> {
    const resource = payload.type;
    const uri = `${toEndpoint(resource)}/${action}`;

    return this.makeRequestWithBody<Resource, Payload, Meta>(uri, 'POST', payload, options);
  }

  public postResourceAction<
    Resource extends ApiResource,
    Payload extends RequiredKeysExhaustive<
      Pick<DeepPartialExhaustive<ApiResource>, 'type' | 'id'>,
      'type' | 'id'
    > = RequiredKeysExhaustive<DeepPartialExhaustive<Resource>, 'type' | 'id'>,
    Meta = object,
  >(
    payload: Payload,
    action: string,
    options: ApiOptions = {},
  ): Promise<ApiResponse<Resource, Meta>> {
    const resource = payload.type;
    const uri = `${toEndpoint(resource)}/${payload.id}/${action}`;
    return this.makeRequestWithBody<Resource, Payload, Meta>(uri, 'POST', payload, options);
  }

  protected async sendRequest(
    uri: ApiEndpoint | string,
    requestOptions: AxiosRequestConfig,
    options: ApiOptions = {},
  ) {
    const params = this.generateParams(options);

    if (options.contentType === 'multipart/form-data') {
      // NOTE: As of Axios 0.27.0, this is natively supported with the toFormData helper.
      // Consider dropping this dependency.
      requestOptions.data = objectToFormData.serialize(requestOptions.data, {
        allowEmptyArrays: !!options.formDataAllowEmptyArrays,
      });
    }

    requestOptions.headers ??= {};
    if (import.meta.env.VITE_COMMIT_SHA)
      requestOptions.headers['Persona-Server-SHA'] = import.meta.env.VITE_COMMIT_SHA;

    try {
      const response = await axios.request({
        ...requestOptions,
        url: `${this.apiRoot}/${uri}${params}`,
      });

      // Allow for easier cross-referencing between Sentry, Datadog, etc.
      setTag('request_id', response?.headers?.['x-request-id']);

      if (response.status === 0 && requestOptions.method) {
        throw new ApiNetworkError({
          method: requestOptions.method,
          uri,
          extra: 'with 0 status code',
        });
      }

      // DELETEs can be empty
      if (
        response.data == null &&
        requestOptions.method &&
        requestOptions.method.toLowerCase() !== 'delete'
      ) {
        throw new ApiNetworkError({
          method: requestOptions.method,
          uri,
          extra: 'with empty response',
        });
      }
      return response;
    } catch (error) {
      if (requestOptions.method) {
        handleAxiosError({ error, method: requestOptions.method, uri });
      } else {
        sentryCaptureException(
          new Error(`Missing request method for Axios error. Error: ${JSON.stringify(error)}`),
        );
      }
    } finally {
      // Don't persist request_id tag for future, possibly unrelated errors
      setTag('request_id', undefined);
    }
  }

  private async makeRequest(uri: ApiEndpoint | string, method: Method, options: ApiOptions = {}) {
    const requestOptions: AxiosRequestConfig = {
      method,
      'axios-retry': options.retryOptions,
      signal: options.signal,
      responseType: options.responseType,
      headers: options.headers,
    };

    const response = await this.sendRequest(uri, requestOptions, options);
    // Can be nullish if response is canceled
    return response?.data;
  }

  private async makeRequestWithBody<
    Resource extends ApiResource,
    Payload extends RequiredKeysPickExhaustive<
      DeepPartialExhaustive<ApiResource>,
      'type'
    > = RequiredKeysExhaustive<DeepPartialExhaustive<Resource>, 'type'>,
    Meta = object,
  >(
    uri: ApiEndpoint | string,
    method: Method,
    payload?: Payload,
    options: ApiOptions = {},
  ): Promise<ApiResponse<Resource, Meta>> {
    const data = { data: payload, meta: options.meta };
    const requestOptions: AxiosRequestConfig = {
      data: data,
      headers: {
        'Content-Type': options.contentType || 'application/json',
        ...options.headers,
      },
      method,
      'axios-retry': options.retryOptions,
      onUploadProgress: options.onUploadProgress,
    };

    const response = await this.sendRequest(uri, requestOptions, options);
    return response?.data;
  }

  private async makeRequestWithMultipleResources<
    Resource extends ApiResource,
    Payload extends RequiredKeysPickExhaustive<
      DeepPartialExhaustive<ApiResource>,
      'type'
    > = RequiredKeysExhaustive<DeepPartialExhaustive<Resource>, 'type'>,
    Meta = object,
  >(
    uri: ApiEndpoint | string,
    method: Method,
    payload: Payload,
    additionalData: ApiResource[] = [],
    options: ApiOptions = {},
  ): Promise<ApiResponse<Resource, Meta>> {
    const requestOptions: AxiosRequestConfig = {
      data: { data: [payload, ...additionalData], meta: options.meta },
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      method,
      'axios-retry': options.retryOptions,
      onUploadProgress: options.onUploadProgress,
    };

    const response = await this.sendRequest(uri, requestOptions, options);
    return response?.data;
  }

  private generateParams(options: ApiOptions): string {
    const params = qs.stringify({
      filter: { ...options.filter },
      page: { ...options.page },
      sort: options.sort,
      query: options.query,
      include: Array.isArray(options.include) ? options.include.join(',') : undefined,
      activation_token: options?.activationToken,
    });

    return params ? `?${params}` : '';
  }
}
